import { ButtonHTMLAttributes } from 'react';
import { BaseButton, BaseButtonProps } from '../base';
import cntl from 'cntl';

interface PrimaryButtonProps extends Omit<BaseButtonProps, 'variant'> {
  // Specifically typed for React.lazy style dynamic imports
  preload?: () => Promise<{ default: () => JSX.Element }>;
  title?: string;
  isLoading?: boolean;
  style?: ButtonHTMLAttributes<HTMLButtonElement>['style'];
}

export const PrimaryButton = ({
  children,
  title,
  className = '',
  preload,
  onMouseEnter,
  onFocus,
  isLoading = false,
  disabled,
  ...props
}: PrimaryButtonProps) => {
  const combinedClasses = cntl`
    min-w-[140px] 
    transition-all 
    duration-200 
    flex justify-center items-center
    ${isLoading ? 'opacity-80 cursor-wait' : 'active:scale-[0.98]'}
    ${className}
  `;

  const handlePreload = () => {
    if (preload && !isLoading) {
      preload().catch((err) => console.error('Preload failed:', err));
    }
  };

  return (
    <BaseButton
      variant="primary"
      className={combinedClasses}
      // Button is disabled if explicitly passed OR if it's currently loading
      disabled={disabled || isLoading}
      onMouseEnter={(e) => {
        handlePreload();
        onMouseEnter?.(e);
      }}
      onFocus={(e) => {
        handlePreload();
        onFocus?.(e);
      }}
      {...props}
    >
      {isLoading && (
        <svg
          className="w-5 h-5 mr-2 text-white animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children ?? title}
    </BaseButton>
  );
};

export const BlackBtn = ({
  className = '',
  children,
  ...props
}: PrimaryButtonProps) => {
  return (
    <PrimaryButton
      className={`px-6 py-4 enabled:bg-black text-white !rounded-full ${className}`}
      {...props}
    >
      <span className="font-bold text-base text-[14px]">{children}</span>
    </PrimaryButton>
  );
};
